/* eslint-disable camelcase */
import { snakeCase } from 'lodash-es';

import {
  ApplicationStatus,
  EDUCATION_LEVEL,
  GenderKeys,
} from '../../common/constants';
import type { ApplicationRankingExposure } from '../../common/interfaces/entities';
import { processEventPropertiesForSensorsData } from '../../common/tracking/sensorsData';
import { TrackingService } from '../../common/tracking/trackingService';
import { trackEvent } from '../GlintsTracking/trackEvent';
import { trackClickEvent } from '../GlintsTracking/tracking';
import {
  getGlintsTrackingIDFromCookie,
  getGlintsTrackingPageName,
} from '../GlintsTracking/trackingSessionUtils';
import { GLINTS_TRACKING_EVENT_QUEUE_KEY } from '../GlintsTracking/types';

export const ChatClickedEventLocation = {
  MANAGE_CANDIDATES_TABLE: 'Manage Candidates Table',
  MANAGE_CANDIDATES_POPUP: 'Manage Candidates Popup',
  HEADER: 'Header',
  APPLICATION_DETAILPAGE: 'Application Detail Page',
} as const;

type ChatClickedEventLocation =
  (typeof ChatClickedEventLocation)[keyof typeof ChatClickedEventLocation];

type ChatClickedEventDataLayer = {
  clickLocation: ChatClickedEventLocation;
  applicationId: string;
  waPresent: boolean;
  unreadCount: number | undefined;
};

export const ApplicationShortlistedPages = {
  APPLICATION_DETAILS_PAGE: 'Application Details Page',
  MANAGE_CANDIDATES: 'Manage Cadidates',
} as const;

type ApplicationShortlistedPages =
  (typeof ApplicationShortlistedPages)[keyof typeof ApplicationShortlistedPages];

export const ShortlistMethod = {
  SINGLE_SHORTLIST: 'Single Shortlist',
  BULK_SHORTLIST: 'Bulk Shortlist',
  SHORTLISTED_FOR_INFO: 'Shortlisted for Info',
} as const;

type ShortlistMethod = (typeof ShortlistMethod)[keyof typeof ShortlistMethod];

type ApplicationsShortlistedEventDataLayer =
  | {
      page: ApplicationShortlistedPages;
      shortlistMethod: ShortlistMethod;
      isMultipleApplications: false;
      waPresent: boolean;
    }
  | {
      page: ApplicationShortlistedPages;
      shortlistMethod: ShortlistMethod;
      isMultipleApplications: true;
      numberOfApplicationShortlisted: number;
    };

export const trackGlobalChatClickedEvent = (
  clickLocation: ChatClickedEventDataLayer['clickLocation'],
  unreadCount: number | undefined
) => {
  TrackingService.logEvent('Chat Clicked', { clickLocation });
  trackClickEvent({
    elementName: 'chats',
    properties: {
      click_location: clickLocation,
      unread_count: unreadCount,
    },
  });
};

export const trackChatClickedEvent = (data: ChatClickedEventDataLayer) => {
  TrackingService.logEvent('Chat Clicked', data);
  trackClickEvent({
    elementName: 'channel',
    properties: {
      click_location: data.clickLocation,
      channel_id: data.applicationId,
      unread_count: data.unreadCount,
    },
  });
};

export const trackApplicationsShortlistedEvent = (
  data: ApplicationsShortlistedEventDataLayer
) => {
  const { isMultipleApplications, ...rest } = data;
  if (isMultipleApplications) {
    TrackingService.logEvent('application_shortlisted', {
      ...rest,
    });
  } else {
    TrackingService.logEvent('application_shortlisted', {
      ...rest,
      numberOfApplicationShortlisted: 1,
    });
  }
};

export const trackManageCandidateViewedEvent = (properties: {
  jobId: string;
  noPendingCdd: number;
  noShortlistedCdd: number;
  noRejectedCdd: number;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    no_pending_cdd: properties.noPendingCdd,
    no_shortlisted_cdd: properties.noShortlistedCdd,
    no_rejected_cdd: properties.noRejectedCdd,
  };

  TrackingService.logEvent('Manage Candidate Viewed', transformedProperties);
};

export const trackChatBannerButtonClickedEvent = (properties: {
  companyId: string;
}) => {
  const transformedProperties = {
    company_id: properties.companyId,
  };
  TrackingService.logEvent('Chat Banner Clicked', transformedProperties);
};

export const trackApplicationDetailsTabSelectedEvent = (properties: {
  tabClicked: string;
  page: string;
}) => {
  const transformedProperties = {
    tab_clicked: properties.tabClicked,
    page: properties.page,
  };

  TrackingService.logEvent(
    'Application Detail Tab Selected',
    transformedProperties
  );
  trackClickEvent({
    elementName: 'application_detail_tab_selected',
    properties: {
      tab_clicked: properties.tabClicked,
      page_clicked: properties.page,
    },
  });
};

export const trackApplicationSelectedEvent = (properties: {
  selectMethod: string;
  applicationId: string;
  stageWhenApplicationSelected: string;
}) => {
  const transformedProperties = {
    select_method: properties.selectMethod,
    application_id: properties.applicationId,
    stage_when_application_selected: properties.stageWhenApplicationSelected,
  };

  TrackingService.logEvent('Application Selected', transformedProperties);
  trackClickEvent({
    elementName: 'application_selected',
    properties: transformedProperties,
  });
};

export const trackApplicationStageTabSelectedEvent = (properties: {
  tabClicked: string;
}) => {
  const transformedProperties = {
    tab_clicked: properties.tabClicked,
  };

  TrackingService.logEvent(
    'Application Stage Tab Selected',
    transformedProperties
  );
  trackClickEvent({
    elementName: 'application_stage_tab_selected',
    properties: transformedProperties,
  });
};

export const trackApplicationRejectedEvent = (properties: {
  page: string;
  rejectMethod: string;
  numberOfApplicationRejected: number;
}) => {
  const transformedProperties = {
    page: properties.page,
    reject_method: properties.rejectMethod,
    number_of_application_rejected: properties.numberOfApplicationRejected,
  };

  TrackingService.logEvent('application_rejected', transformedProperties);
};

export type EventProperties = {
  locations: string[] | null;
  cities: string[] | null;
  country: string | null;
  minAge: number | null;
  maxAge: number | null;
  gender: GenderKeys | null;
  minSalary: number | null;
  maxSalary: number | null;
  workDurationInMonthsMin: number | null;
  workDurationInMonthsMax: number | null;
  educationLevel: EDUCATION_LEVEL | null;
  sources: string[] | null;
};

export const trackFiltersAppliedEvent = (properties: EventProperties) => {
  const transformedProperties =
    processEventPropertiesForSensorsData(properties);

  TrackingService.logEvent('Filters Applied Clicked', transformedProperties);
  trackClickEvent({
    elementName: 'filters_applied',
    properties: transformedProperties,
  });
};

export const trackPageBackButtonClickedEvent = (props: {
  jobId: string;
  applicationId: string | null;
}) => {
  TrackingService.logEvent('Page Back Button Clicked');
  trackClickEvent({
    elementName: 'page_back_button_clicked',
    properties: {
      job_id: props.jobId,
      application_id: props.applicationId,
    },
  });
};

export const trackPageSelectedEvent = (properties: { pageClicked: number }) => {
  const transformedProperties = {
    page_clicked: properties.pageClicked,
  };

  TrackingService.logEvent('Page Selected', transformedProperties);
  trackClickEvent({
    elementName: 'application_list_pagination',
    properties: transformedProperties,
  });
};

export const trackResumeDownloadedEvent = (properties: {
  stageOfApplication: string;
}) => {
  const transformedProperties = {
    stage_of_application: properties.stageOfApplication,
  };

  TrackingService.logEvent('Resume Downloaded', transformedProperties);
};

// Ranking telemetry is optional: bound its storage work and leave a full or
// invalid existing queue untouched. Character length is checked before parsing.
const MAX_RANKING_QUEUE_LENGTH = 64 * 1024;
const canQueueRankingEvent = () => {
  const rawQueue = localStorage.getItem(GLINTS_TRACKING_EVENT_QUEUE_KEY);
  return (
    !rawQueue ||
    (rawQueue.length < MAX_RANKING_QUEUE_LENGTH &&
      Array.isArray(JSON.parse(rawQueue)))
  );
};

const logRankingEvents = (
  name: string,
  eventProperties: Array<Record<string, unknown>>
) => {
  try {
    if (!window.GlintsTracking || eventProperties.length === 0) return;
    const sessionId = getGlintsTrackingIDFromCookie();
    const page = getGlintsTrackingPageName(document);
    const pagePath = window.location.pathname;
    const events = eventProperties.map(properties => ({
      event_type: name,
      properties: {
        ...(sessionId && { session_id: sessionId }),
        ...(page && { page, page_path: pagePath }),
        ...Object.fromEntries(
          Object.entries(properties).map(([key, value]) => [
            snakeCase(key),
            value,
          ])
        ),
      },
    }));
    // Keep queue reads, parsing and writes out of the core action callback.
    // Capture the original page/session above before navigation can change them.
    window.setTimeout(() => {
      try {
        for (const event of events) {
          if (!canQueueRankingEvent()) break;
          trackEvent(event);
        }
      } catch {
        // Storage or tracker failure must not interrupt a committed action.
      }
    }, 0);
  } catch {
    // Scheduling failure must not interrupt a sort change or shortlist.
  }
};

export const trackApplicationSortChanged = (data: {
  jobId: string;
  fromOrder: string;
  toOrder: string;
  rankingExposure?: ApplicationRankingExposure;
}) => {
  if (data.fromOrder === data.toOrder) return;
  logRankingEvents('application_sort_changed', [
    {
      jobId: data.jobId,
      fromOrder: data.fromOrder,
      toOrder: data.toOrder,
      ...(data.rankingExposure && {
        fromEffectiveOrder: data.rankingExposure.effectiveOrder,
        fromScoreVersion: data.rankingExposure.scoreVersion,
      }),
    },
  ]);
};

export const trackSuccessfulShortlists = (
  updates: Array<{
    id: string;
    JobId: string;
    status: ApplicationStatus;
    isShortlistTransition?: boolean;
  }>,
  submitted: Array<{
    applicationId: string;
    rankingExposure?: ApplicationRankingExposure;
  }>,
  source: string
) => {
  const submittedById = new Map(
    submitted.map(application => [application.applicationId, application])
  );
  const trackedIds = new Set<string>();
  const events: Array<Record<string, unknown>> = [];
  updates.forEach(update => {
    const application = submittedById.get(update.id);
    if (
      !application ||
      !update.isShortlistTransition ||
      update.status !== ApplicationStatus.IN_REVIEW ||
      trackedIds.has(update.id)
    )
      return;
    trackedIds.add(update.id);
    events.push({
      applicationId: update.id,
      jobId: update.JobId,
      source,
      exposureAvailable: Boolean(application.rankingExposure),
      ...application.rankingExposure,
    });
  });
  logRankingEvents('application_shortlist_succeeded', events);
};
